import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/app/rootReducer";
import {
  fetchProductStoreOrdersAction,
  resetProductStoreOrders,
} from "../slices/productStoreOrdersSlice";
import {
  FetchProductStoreOrdersRequest,
  ProductStoreOrderStatus,
} from "../api/fetchProductStoreOrders/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";
import { PaginationState } from "@/components/Datatable/components/DataTablePagination/types";

export interface OrderListFilters {
  searchTerm: string;
  status: ProductStoreOrderStatus[];
  companyUuid?: string;
  companyName?: string;
  startDate?: string;
  endDate?: string;
}

const DEFAULT_FILTERS: OrderListFilters = {
  searchTerm: "",
  status: [],
};

const DEFAULT_PAGINATION: PaginationState = {
  pageIndex: 0,
  pageSize: 25,
};

const DEFAULT_SORTING: SortingState = [{ id: "finalizedAt", desc: true }];

export function useOrderListPage() {
  const dispatch = useDispatch();
  const { orders, totalCount, loading, error } = useSelector(
    (state: RootState) => state.productStoreOrders,
  );

  const [pagination, setPagination] =
    useState<PaginationState>(DEFAULT_PAGINATION);
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const [filters, setFilters] = useState<OrderListFilters>(DEFAULT_FILTERS);

  useEffect(() => {
    const controller = new AbortController();

    const requestData: FetchProductStoreOrdersRequest = {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy:
        sorting.length > 0
          ? (sorting[0].id as FetchProductStoreOrdersRequest["sortBy"])
          : "finalizedAt",
      sortOrder: sorting.length > 0 && sorting[0].desc ? "DESC" : "ASC",
      searchTerm: filters.searchTerm || undefined,
      status: filters.status.length > 0 ? filters.status : undefined,
      companyUuid: filters.companyUuid || undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
    };

    dispatch(fetchProductStoreOrdersAction(requestData, controller.signal));

    return () => {
      controller.abort();
    };
  }, [dispatch, pagination, sorting, filters]);

  useEffect(() => {
    return () => {
      dispatch(resetProductStoreOrders());
    };
  }, [dispatch]);

  const handlePaginationChange: OnChangeFn<PaginationState> = useCallback(
    (updaterOrValue) => {
      setPagination((old) =>
        typeof updaterOrValue === "function"
          ? updaterOrValue(old)
          : updaterOrValue,
      );
    },
    [],
  );

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      setSorting((old) =>
        typeof updaterOrValue === "function"
          ? updaterOrValue(old)
          : updaterOrValue,
      );
    },
    [],
  );

  const handleFiltersChange = useCallback(
    (partial: Partial<OrderListFilters>) => {
      setFilters((prev) => ({ ...prev, ...partial }));
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [],
  );

  return {
    orders,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    filters,
    handlePaginationChange,
    handleSortingChange,
    handleFiltersChange,
  };
}
